import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Shield,
  Search,
  AlertTriangle,
  Eye,
  Globe,
  GitBranch,
  Zap,
  Ban,
  FileText,
  Users,
  Star,
  GitFork,
  ExternalLink,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Settings
} from 'lucide-react';
import { fetchAgents, fetchSentienceEvents, getApiKey } from '../lib/api';

function apiHeaders(extra?: Record<string, string>): Record<string, string> {
  const key = getApiKey() || '';
  return { 'Content-Type': 'application/json', ...(key ? { 'X-API-Key': key } : {}), ...extra };
}
import { isProduction } from '../lib/production-guard';
import { useDemoMode } from './DemoContext';
import { SAMPLE_DATA_BADGE, sampleDataDisclaimer } from '../lib/disclaimers.generated';

function useSafeDemoMode() {
  try {
    return useDemoMode();
  } catch {
    return { isDemoMode: false, activeScenario: null, scenarios: [], setIsDemoMode: () => {}, setActiveScenario: () => {} };
  }
}

interface ThreatEntry {
  id: string;
  name: string;
  url: string;
  description: string;
  riskScore: number;
  threatType: string;
  similarity: number;
  detectedPatterns: string[];
  platform: string;
  owner: {
    username: string;
    type: string;
    accountAge: number;
    reputation: number;
  };
  analysis: {
    potentialOrigin: string;
    recommendedAction: string;
    architecturalSimilarity: number;
    terminologyOverlap: number;
  };
  stars: number;
  forks: number;
  language: string;
  createdAt: string;
  updatedAt: string;
}

interface ScanStatus {
  isScanning: boolean;
  lastScan: string | null;
  nextScan: string | null;
  threatsDetected: number;
  scanDuration: number;
}

interface OSINTStatistics {
  threats: {
    total: number;
    byType: Record<string, number>;
    riskDistribution: {
      critical: number;
      high: number;
      medium: number;
      low: number;
    };
  };
  signals: {
    totalSignals: number;
    signalsBySeverity: Record<string, number>;
  };
  scanning: {
    lastScan: string | null;
    nextScan: string | null;
    scanInterval: number;
    scanEnabled: boolean;
    platformCoverage: number;
    monitoredPatterns: number;
  };
}

export default function OSINTThreatDetectionPanel() {
  const { isDemoMode: globalDemoMode, activeScenario } = useSafeDemoMode();
  const [threats, setThreats] = useState<ThreatEntry[]>([]);
  const [statistics, setStatistics] = useState<OSINTStatistics | null>(null);
  const [scanStatus, setScanStatus] = useState<ScanStatus>({
    isScanning: false,
    lastScan: null,
    nextScan: null,
    threatsDetected: 0,
    scanDuration: 0
  });
  const [selectedThreat, setSelectedThreat] = useState<ThreatEntry | null>(null);
  const [filterType, setFilterType] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [personaLevel] = useState('Operator');
  const [, setOsintAgents] = useState<any[]>([]);
  const [configureStatus, setConfigureStatus] = useState<'idle' | 'open' | 'saved'>('idle');
  const [deepAnalysisStatus, setDeepAnalysisStatus] = useState<'idle' | 'running' | 'done'>('idle');
  const [reportStatus, setReportStatus] = useState<'idle' | 'generating' | 'done'>('idle');
  const [isUsingDemoData, setIsUsingDemoData] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Apply demo scenario threat data
  useEffect(() => {
    if (globalDemoMode && activeScenario) {
      const scenarioThreats: ThreatEntry[] = [];
      const baseThreat = (id: string, name: string, desc: string, risk: number, type: string): ThreatEntry => ({
        id, name, url: '#', description: desc, riskScore: risk, threatType: type, similarity: risk * 0.85,
        detectedPatterns: ['governance framework', 'agent orchestration'],
        platform: 'github', owner: { username: 'demo', type: 'user', accountAge: 30, reputation: 100 },
        analysis: { potentialOrigin: 'detected', recommendedAction: risk >= 0.9 ? 'immediate_action' : 'investigate', architecturalSimilarity: risk * 0.9, terminologyOverlap: risk * 0.8 },
        stars: Math.floor(risk * 200), forks: Math.floor(risk * 50), language: 'TypeScript',
        createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
      });

      if (activeScenario.id === 'crisis-simulation') {
        scenarioThreats.push(
          baseThreat('crisis-1', 'active-breach-vector', 'CRITICAL -- Active exploitation attempt detected against governance API', 0.97, 'INDUSTRIAL_ESPIONAGE'),
          baseThreat('crisis-2', 'shadow-agent-cluster', 'Ungoverned agent cluster discovered in production VPC', 0.91, 'ARCHITECTURAL_COPY'),
          baseThreat('crisis-3', 'dns-exfiltration-probe', 'DNS-based data exfiltration probe targeting agent metadata', 0.88, 'PATTERN_MIMICRY'),
          baseThreat('crisis-4', 'credential-harvest-attempt', 'Credential harvesting attempt via spoofed MCP endpoint', 0.85, 'INDUSTRIAL_ESPIONAGE'),
          baseThreat('crisis-5', 'supply-chain-anomaly', 'Anomalous dependency update in monitored agent framework', 0.82, 'COMPETITIVE_CLONE'),
        );
      } else if (activeScenario.id === 'enterprise-ops') {
        scenarioThreats.push(
          baseThreat('ent-1', 'competitive-platform-alpha', 'Competitive platform with overlapping terminology detected', 0.72, 'COMPETITIVE_CLONE'),
          baseThreat('ent-2', 'pattern-similarity-beta', 'Open-source project with partial architectural similarity', 0.58, 'PATTERN_MIMICRY'),
        );
      } else if (activeScenario.id === 'growth-showcase') {
        scenarioThreats.push(
          baseThreat('growth-1', 'new-entrant-monitor', 'New market entrant detected -- monitoring for IP overlap', 0.45, 'PATTERN_MIMICRY'),
        );
      }

      setThreats(scenarioThreats);
      setIsUsingDemoData(true);
      setScanStatus(prev => ({ ...prev, threatsDetected: scenarioThreats.length }));
      setStatistics({
        threats: {
          total: scenarioThreats.length,
          byType: {},
          riskDistribution: {
            critical: scenarioThreats.filter(t => t.riskScore >= 0.9).length,
            high: scenarioThreats.filter(t => t.riskScore >= 0.8 && t.riskScore < 0.9).length,
            medium: scenarioThreats.filter(t => t.riskScore >= 0.6 && t.riskScore < 0.8).length,
            low: scenarioThreats.filter(t => t.riskScore < 0.6).length,
          },
        },
        signals: { totalSignals: scenarioThreats.length, signalsBySeverity: {} },
        scanning: { lastScan: new Date().toISOString(), nextScan: null, scanInterval: 3600000, scanEnabled: true, platformCoverage: 5, monitoredPatterns: 34 },
      });
    }
  }, [globalDemoMode, activeScenario]);

  // Load real API data with simulated fallback
  useEffect(() => {
    if (globalDemoMode) return; // Skip API polling during demo mode
    loadFromAPI();
    pollRef.current = setInterval(loadFromAPI, 15000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [globalDemoMode]);

  const loadFromAPI = async () => {
    const [agentsData, sentienceData] = await Promise.all([
      fetchAgents(),
      fetchSentienceEvents(),
    ]);

    if (agentsData) {
      const agents = Array.isArray(agentsData) ? agentsData : agentsData.agents || [];
      const osint = agents.filter((a: any) =>
        a.id === 'mod-015' || a.id === 'mod-034' ||
        (a.name && (a.name.includes('Red Flag') || a.name.includes('Security')))
      );
      setOsintAgents(osint);
    }

    if (sentienceData) {
      const events = Array.isArray(sentienceData) ? sentienceData : sentienceData.events || [];
      if (events.length > 0) {
        const mapped: ThreatEntry[] = events.map((evt: any, idx: number) => ({
          id: evt.id || `sentience_${idx}`,
          name: evt.type || evt.name || 'Sentience Event',
          url: evt.source || '#',
          description: evt.description || evt.message || 'Detected sentience event from runtime monitoring',
          riskScore: evt.severity === 'critical' ? 0.95 : evt.severity === 'high' ? 0.85 : evt.severity === 'medium' ? 0.7 : 0.5,
          threatType: evt.type || 'PATTERN_MIMICRY',
          similarity: evt.confidence || 0.6,
          detectedPatterns: evt.patterns || ['sentience marker'],
          platform: 'internal',
          owner: { username: 'system', type: 'agent', accountAge: 0, reputation: 0 },
          analysis: {
            potentialOrigin: 'detected',
            recommendedAction: evt.severity === 'critical' ? 'immediate_action' : 'investigate',
            architecturalSimilarity: 0,
            terminologyOverlap: 0,
          },
          stars: 0,
          forks: 0,
          language: 'N/A',
          createdAt: evt.timestamp || new Date().toISOString(),
          updatedAt: evt.timestamp || new Date().toISOString(),
        }));
        setThreats(mapped);
        setScanStatus(prev => ({ ...prev, threatsDetected: mapped.length }));
        setStatistics(prev => prev ? {
          ...prev,
          threats: { ...prev.threats, total: mapped.length },
          signals: { ...prev.signals, totalSignals: mapped.length },
        } : prev);
        setIsUsingDemoData(false);
        return; // Real data loaded, skip fallback
      }
    }

    // Fallback: keep simulated data if no real data available (dev only)
    if (threats.length === 0 && !isProduction()) {
      setIsUsingDemoData(true);
      initializeMockData();
      loadStatistics();
    }
  };

  const initializeMockData = () => {
    const mockThreats: ThreatEntry[] = [
      {
        id: 'threat_001',
        name: 'sim-governance-layer',
        url: 'https://example.com/demo/sim-governance-layer',
        description: 'Simulated threat -- multi-agent governance platform with overlapping terminology',
        riskScore: 0.89,
        threatType: 'ARCHITECTURAL_COPY',
        similarity: 0.76,
        detectedPatterns: ['multi-agent orchestration', 'governance framework', 'module injection', 'persona routing'],
        platform: 'github',
        owner: {
          username: 'demo-account-a',
          type: 'user',
          accountAge: 15,
          reputation: 234
        },
        analysis: {
          potentialOrigin: 'reverse_engineered',
          recommendedAction: 'legal_review',
          architecturalSimilarity: 0.82,
          terminologyOverlap: 0.71
        },
        stars: 127,
        forks: 23,
        language: 'TypeScript',
        createdAt: '2026-05-01T10:30:00Z',
        updatedAt: '2026-05-01T08:45:00Z'
      },
      {
        id: 'threat_002',
        name: 'sim-agent-audit-platform',
        url: 'https://example.com/demo/sim-agent-audit-platform',
        description: 'Simulated threat -- enterprise AI governance platform with agent audit trails',
        riskScore: 0.72,
        threatType: 'COMPETITIVE_CLONE',
        similarity: 0.68,
        detectedPatterns: ['enterprise AI governance platform', 'agent governance', 'audit trails'],
        platform: 'gitlab',
        owner: {
          username: 'demo-org-b',
          type: 'organization',
          accountAge: 180,
          reputation: 1250
        },
        analysis: {
          potentialOrigin: 'inspired',
          recommendedAction: 'investigate',
          architecturalSimilarity: 0.65,
          terminologyOverlap: 0.72
        },
        stars: 89,
        forks: 15,
        language: 'Python',
        createdAt: '2026-04-15T14:20:00Z',
        updatedAt: '2026-05-01T16:30:00Z'
      },
      {
        id: 'threat_003',
        name: 'sim-direct-copy',
        url: 'https://example.com/demo/sim-direct-copy',
        description: 'Simulated threat -- direct architectural copy with minimal modifications',
        riskScore: 0.95,
        threatType: 'INDUSTRIAL_ESPIONAGE',
        similarity: 0.94,
        detectedPatterns: ['governance framework', 'module registry', 'identity management', 'audit chain'],
        platform: 'github',
        owner: {
          username: 'demo-account-c',
          type: 'user',
          accountAge: 7,
          reputation: 12
        },
        analysis: {
          potentialOrigin: 'copied',
          recommendedAction: 'immediate_action',
          architecturalSimilarity: 0.96,
          terminologyOverlap: 0.92
        },
        stars: 8,
        forks: 2,
        language: 'TypeScript',
        createdAt: '2026-04-20T09:15:00Z',
        updatedAt: '2026-05-01T11:20:00Z'
      }
    ];

    setThreats(mockThreats);
    setScanStatus({
      isScanning: false,
      lastScan: '2026-05-01T12:00:00Z',
      nextScan: '2026-05-01T13:00:00Z',
      threatsDetected: mockThreats.length,
      scanDuration: 2340
    });
  };

  const loadStatistics = () => {
    const mockStats: OSINTStatistics = {
      threats: {
        total: 8,
        byType: {
          'ARCHITECTURAL_COPY': 2,
          'COMPETITIVE_CLONE': 3,
          'INDUSTRIAL_ESPIONAGE': 1,
          'PATTERN_MIMICRY': 2
        },
        riskDistribution: {
          critical: 1,
          high: 2,
          medium: 3,
          low: 2
        }
      },
      signals: {
        totalSignals: 12,
        signalsBySeverity: {
          'CRITICAL': 1,
          'HIGH': 3,
          'MEDIUM': 5,
          'LOW': 3
        }
      },
      scanning: {
        lastScan: '2026-05-01T12:00:00Z',
        nextScan: '2026-05-01T13:00:00Z',
        scanInterval: 3600000,
        scanEnabled: true,
        platformCoverage: 5,
        monitoredPatterns: 34
      }
    };

    setStatistics(mockStats);
  };

  const handleManualScan = async () => {
    setScanStatus(prev => ({ ...prev, isScanning: true }));
    const scanStart = Date.now();

    try {
      const res = await fetch('/api/blackbox/sentience/events', { headers: apiHeaders() });
      if (res.ok) {
        const data = await res.json();
        const events = Array.isArray(data) ? data : data.events || [];
        if (events.length > 0) {
          const mapped: ThreatEntry[] = events.map((evt: any, idx: number) => ({
            id: evt.id || `scan_${idx}`,
            name: evt.type || evt.name || 'Sentience Event',
            url: evt.source || '#',
            description: evt.description || evt.message || 'Detected during manual scan',
            riskScore: evt.severity === 'critical' ? 0.95 : evt.severity === 'high' ? 0.85 : 0.7,
            threatType: evt.type || 'PATTERN_MIMICRY',
            similarity: evt.confidence || 0.6,
            detectedPatterns: evt.patterns || ['sentience marker'],
            platform: 'internal',
            owner: { username: 'system', type: 'agent', accountAge: 0, reputation: 0 },
            analysis: {
              potentialOrigin: 'detected',
              recommendedAction: 'investigate',
              architecturalSimilarity: 0,
              terminologyOverlap: 0,
            },
            stars: 0, forks: 0, language: 'N/A',
            createdAt: evt.timestamp || new Date().toISOString(),
            updatedAt: evt.timestamp || new Date().toISOString(),
          }));
          setThreats(mapped);
        }
      }
    } catch {
      // API unavailable -- scan completes with existing data
    }

    setScanStatus(prev => ({
      ...prev,
      isScanning: false,
      lastScan: new Date().toISOString(),
      nextScan: new Date(Date.now() + 3600000).toISOString(),
      scanDuration: Date.now() - scanStart,
    }));
  };

  const handleDismissThreat = (threatId: string) => {
    setThreats(prev => prev.filter(t => t.id !== threatId));
    setSelectedThreat(null);
  };

  const filteredThreats = threats.filter(threat => {
    if (filterType !== 'all' && threat.threatType !== filterType) return false;
    if (filterSeverity !== 'all') {
      const severity = threat.riskScore >= 0.9 ? 'critical' :
                     threat.riskScore >= 0.8 ? 'high' :
                     threat.riskScore >= 0.6 ? 'medium' : 'low';
      if (severity !== filterSeverity) return false;
    }
    if (searchQuery && !threat.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !threat.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const getThreatSeverityColor = (riskScore: number) => {
    if (riskScore >= 0.9) return 'text-ag-critical bg-red-900 border-red-600';
    if (riskScore >= 0.8) return 'text-orange-400 bg-orange-900 border-orange-600';
    if (riskScore >= 0.6) return 'text-ag-warning bg-yellow-900 border-yellow-600';
    return 'text-ag-active bg-green-900 border-green-600';
  };

  const getThreatTypeIcon = (type: string) => {
    switch (type) {
      case 'INDUSTRIAL_ESPIONAGE': return <Ban className="w-4 h-4 text-ag-critical" />;
      case 'ARCHITECTURAL_COPY': return <GitBranch className="w-4 h-4 text-orange-400" />;
      case 'COMPETITIVE_CLONE': return <Users className="w-4 h-4 text-ag-warning" />;
      case 'PATTERN_MIMICRY': return <Eye className="w-4 h-4 text-blue-400" />;
      default: return <AlertTriangle className="w-4 h-4 text-[#A8A29E]" />;
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'github': return <GitBranch className="w-4 h-4" />;
      case 'gitlab': return <Globe className="w-4 h-4" />;
      default: return <Globe className="w-4 h-4" />;
    }
  };

  const getRecommendedActionIcon = (action: string) => {
    switch (action) {
      case 'immediate_action': return <Zap className="w-4 h-4 text-ag-critical" />;
      case 'legal_review': return <FileText className="w-4 h-4 text-orange-400" />;
      case 'investigate': return <Search className="w-4 h-4 text-ag-warning" />;
      case 'monitor': return <Eye className="w-4 h-4 text-ag-active" />;
      default: return <AlertCircle className="w-4 h-4 text-[#A8A29E]" />;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2 flex items-center">
            🔍 OSINT Threat Detection{isUsingDemoData && <span className="font-body text-lg font-normal text-amber-400 ml-2" title={sampleDataDisclaimer("dashboard")}>({SAMPLE_DATA_BADGE})</span>}
          </h2>
          <p className="text-[#A8A29E]">Monitor open-source intelligence for IP replication and competitive threats</p>
          {globalDemoMode && (
            <p className="text-xs text-gray-500 italic mt-1">Shadow AI discovery. Identifies ungoverned agents in your environment through DNS, MDM, and cloud log analysis.</p>
          )}
        </div>
        <div className="flex items-center space-x-3">
          {/* The colour was static copper for BOTH branches, so "Active Monitoring" and
              "Monitoring Paused" -- opposite states -- rendered identically, in the brand
              accent. Colour carried no information and the accent is not a state. Now
              green when scanning, dim when it is not. */}
          <Badge
            variant="outline"
            className={statistics?.scanning.scanEnabled
              ? 'text-ag-active border-ag-active'
              : 'text-ag-inactive border-ag-inactive'}
          >
            {statistics?.scanning.scanEnabled ? 'Active Monitoring' : 'Monitoring Paused'}
          </Badge>
          {/* Access tier is categorical, not a governance state, so it correctly uses the
              brand accent. Spelled `ag-accent` rather than `purple-400`: tailwind.config.js
              remaps the whole purple/violet/indigo scale to copper, so `purple-400` already
              rendered #C2714F and only read as purple in the source. */}
          <Badge variant="outline" className="text-ag-accent border-ag-accent">
            {personaLevel} Access
          </Badge>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Active Threats</p>
                <p className="text-2xl font-bold text-ag-critical">{statistics?.threats.total || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-ag-critical opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Critical Risks</p>
                <p className="text-2xl font-bold text-ag-critical">{statistics?.threats.riskDistribution.critical || 0}</p>
              </div>
              <Ban className="w-8 h-8 text-ag-critical opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Platforms Monitored</p>
                <p className="text-2xl font-bold text-[#C2714F]">{statistics?.scanning.platformCoverage || 0}</p>
              </div>
              <Globe className="w-8 h-8 text-[#C2714F] opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Pattern Signatures</p>
                <p className="text-2xl font-bold text-green-400">{statistics?.scanning.monitoredPatterns || 0}</p>
              </div>
              <Search className="w-8 h-8 text-green-400 opacity-60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scan Control Panel */}
      <Card className="bg-gray-900 border-[#3A352F]">
        <CardHeader>
          <CardTitle className="text-white flex items-center">
            <Zap className="w-5 h-5 mr-2 text-[#C2714F]" />
            Threat Scanning Control
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-[#A8A29E] mb-1">Last Scan</p>
              <p className="text-white">
                {scanStatus.lastScan ? new Date(scanStatus.lastScan).toLocaleString() : 'Never'}
              </p>
            </div>
            <div>
              <p className="text-sm text-[#A8A29E] mb-1">Next Scheduled</p>
              <p className="text-white">
                {scanStatus.nextScan ? new Date(scanStatus.nextScan).toLocaleString() : 'Not scheduled'}
              </p>
            </div>
            <div>
              <p className="text-sm text-[#A8A29E] mb-1">Scan Duration</p>
              <p className="text-white">{scanStatus.scanDuration}ms</p>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <Button
              onClick={handleManualScan}
              disabled={scanStatus.isScanning}
              className="bg-[#C2714F] hover:bg-[#D4825F] disabled:opacity-50"
            >
              {scanStatus.isScanning ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Manual Scan
                </>
              )}
            </Button>
            
            <Button
              variant="outline"
              className="border-[#4A443D] text-[#A8A29E] hover:bg-[#3A352F]"
              onClick={() => setConfigureStatus(s => s === 'open' ? 'idle' : 'open')}
            >
              <Settings className="w-4 h-4 mr-2" />
              Configure
            </Button>
          </div>

          {configureStatus !== 'idle' && (
            <div className="mt-4 p-3 bg-[#292524] rounded-lg border border-[#3A352F] space-y-3">
              {configureStatus === 'saved' ? (
                <div className="flex items-center gap-2 text-ag-active">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">Scan configuration saved successfully.</span>
                  <Button size="sm" variant="outline" className="ml-auto text-xs" onClick={() => setConfigureStatus('idle')}>Dismiss</Button>
                </div>
              ) : (
                <>
                  <h4 className="text-white text-sm font-medium">Scan Configuration</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[#A8A29E]">Scan Interval</label>
                      <select className="w-full bg-gray-900 border border-[#3A352F] text-white px-2 py-1 rounded text-sm mt-1">
                        <option>Every hour</option>
                        <option>Every 6 hours</option>
                        <option>Every 24 hours</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-[#A8A29E]">Risk Threshold</label>
                      <select className="w-full bg-gray-900 border border-[#3A352F] text-white px-2 py-1 rounded text-sm mt-1">
                        <option>Low (0.4+)</option>
                        <option>Medium (0.6+)</option>
                        <option>High (0.8+)</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-[#C2714F] hover:bg-[#D4825F]"
                      onClick={async () => {
                        try { await fetch('/api/claw/health', { headers: apiHeaders() }); } catch {}
                        setConfigureStatus('saved');
                      }}
                    >Save Configuration</Button>
                    <Button size="sm" variant="outline" onClick={() => setConfigureStatus('idle')}>Cancel</Button>
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Threat Analysis Dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Threat List */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-white">Detected Threats</h3>
            <div className="flex items-center space-x-3">
              <Input
                placeholder="Search threats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 bg-[#292524] border-[#4A443D] text-white"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex space-x-4">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="bg-[#292524] border-[#4A443D] text-white rounded px-3 py-2"
            >
              <option value="all">All Types</option>
              <option value="INDUSTRIAL_ESPIONAGE">Industrial Espionage</option>
              <option value="ARCHITECTURAL_COPY">Architectural Copy</option>
              <option value="COMPETITIVE_CLONE">Competitive Clone</option>
              <option value="PATTERN_MIMICRY">Pattern Mimicry</option>
            </select>

            <select
              value={filterSeverity}
              onChange={(e) => setFilterSeverity(e.target.value)}
              className="bg-[#292524] border-[#4A443D] text-white rounded px-3 py-2"
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          {/* Threat Cards */}
          <div className="space-y-4">
            {filteredThreats.map(threat => (
              <Card 
                key={threat.id} 
                className={`cursor-pointer transition-all ${
                  selectedThreat?.id === threat.id 
                    ? 'bg-[#7A3E28]/20 border-[#C2714F]' 
                    : 'bg-gray-900 border-[#3A352F] hover:bg-[#292524]'
                }`}
                onClick={() => setSelectedThreat(threat)}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center space-x-3">
                      {getThreatTypeIcon(threat.threatType)}
                      <div>
                        <h4 className="text-white font-medium">{threat.name}</h4>
                        <div className="flex items-center space-x-2 text-sm text-[#A8A29E]">
                          {getPlatformIcon(threat.platform)}
                          <span>{threat.platform}</span>
                          <span>•</span>
                          <span>{threat.owner.username}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Badge className={getThreatSeverityColor(threat.riskScore)}>
                        {threat.riskScore >= 0.9 ? 'Critical' :
                         threat.riskScore >= 0.8 ? 'High' :
                         threat.riskScore >= 0.6 ? 'Medium' : 'Low'}
                      </Badge>
                      {getRecommendedActionIcon(threat.analysis.recommendedAction)}
                    </div>
                  </div>

                  <p className="text-[#A8A29E] text-sm mb-3 line-clamp-2">{threat.description}</p>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                      <span className="text-[#A8A29E]">Risk Score:</span>
                      <div className="text-white font-medium">{(threat.riskScore * 100).toFixed(1)}%</div>
                    </div>
                    <div>
                      <span className="text-[#A8A29E]">Similarity:</span>
                      <div className="text-white font-medium">{(threat.similarity * 100).toFixed(1)}%</div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Star className="w-3 h-3 text-yellow-400" />
                      <span className="text-white">{threat.stars}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <GitFork className="w-3 h-3 text-blue-400" />
                      <span className="text-white">{threat.forks}</span>
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="flex flex-wrap gap-1">
                      {threat.detectedPatterns.slice(0, 3).map((pattern, index) => (
                        <span key={index} className="text-xs bg-red-900/20 text-ag-critical px-2 py-1 rounded">
                          {pattern}
                        </span>
                      ))}
                      {threat.detectedPatterns.length > 3 && (
                        <span className="text-xs text-[#A8A29E]">+{threat.detectedPatterns.length - 3} more</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredThreats.length === 0 && (
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardContent className="p-8 text-center">
                <Shield className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
                <h4 className="text-white font-medium mb-2">No threats detected</h4>
                <p className="text-[#A8A29E] text-sm">
                  Your monitoring systems are active and protecting your intellectual property
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Threat Details Panel */}
        <div className="space-y-6">
          {selectedThreat ? (
            <>
              <Card className="bg-gray-900 border-[#3A352F]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center justify-between">
                    <span>Threat Analysis</span>
                    <a 
                      href={selectedThreat.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#C2714F] hover:text-[#D4825F]"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="text-white font-medium mb-2">{selectedThreat.name}</h4>
                    <p className="text-[#A8A29E] text-sm">{selectedThreat.description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-[#A8A29E]">Type:</span>
                      <div className="text-white">{selectedThreat.threatType.replace('_', ' ')}</div>
                    </div>
                    <div>
                      <span className="text-[#A8A29E]">Language:</span>
                      <div className="text-white">{selectedThreat.language}</div>
                    </div>
                    <div>
                      <span className="text-[#A8A29E]">Owner Type:</span>
                      <div className="text-white capitalize">{selectedThreat.owner.type}</div>
                    </div>
                    <div>
                      <span className="text-[#A8A29E]">Account Age:</span>
                      <div className="text-white">{selectedThreat.owner.accountAge} days</div>
                    </div>
                  </div>

                  <div>
                    <span className="text-[#A8A29E] text-sm">Potential Origin:</span>
                    <div className="text-white capitalize">{selectedThreat.analysis.potentialOrigin.replace('_', ' ')}</div>
                  </div>

                  <div>
                    <span className="text-[#A8A29E] text-sm">Recommended Action:</span>
                    <div className="flex items-center space-x-2 mt-1">
                      {getRecommendedActionIcon(selectedThreat.analysis.recommendedAction)}
                      <span className="text-white capitalize">{selectedThreat.analysis.recommendedAction.replace('_', ' ')}</span>
                    </div>
                  </div>

                  <div>
                    <span className="text-[#A8A29E] text-sm">Detected Patterns:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedThreat.detectedPatterns.map((pattern, index) => (
                        <span key={index} className="text-xs bg-red-900/20 text-ag-critical px-2 py-1 rounded">
                          {pattern}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#A8A29E]">Architectural Similarity</span>
                      <span className="text-white">{(selectedThreat.analysis.architecturalSimilarity * 100).toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-[#3A352F] rounded-full h-2">
                      <div 
                        className="bg-orange-400 h-2 rounded-full" 
                        style={{ width: `${selectedThreat.analysis.architecturalSimilarity * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#A8A29E]">Terminology Overlap</span>
                      <span className="text-white">{(selectedThreat.analysis.terminologyOverlap * 100).toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-[#3A352F] rounded-full h-2">
                      <div 
                        className="bg-red-400 h-2 rounded-full" 
                        style={{ width: `${selectedThreat.analysis.terminologyOverlap * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-900 border-[#3A352F]">
                <CardHeader>
                  <CardTitle className="text-white">Enforcement Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    variant="outline"
                    className={`w-full ${deepAnalysisStatus === 'done' ? 'border-ag-active text-ag-active' : 'border-[#4A443D] text-[#A8A29E] hover:bg-[#3A352F]'}`}
                    disabled={deepAnalysisStatus === 'running'}
                    onClick={async () => {
                      setDeepAnalysisStatus('running');
                      try { await fetch('/api/claw/health', { headers: apiHeaders() }); } catch {}
                      setDeepAnalysisStatus('done');
                      setTimeout(() => setDeepAnalysisStatus('idle'), 3000);
                    }}
                  >
                    {deepAnalysisStatus === 'running' ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Analyzing...</>
                    ) : deepAnalysisStatus === 'done' ? (
                      <><CheckCircle className="w-4 h-4 mr-2" />Analysis Complete</>
                    ) : (
                      <><Eye className="w-4 h-4 mr-2" />Deep Analysis</>
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    className={`w-full ${reportStatus === 'done' ? 'border-ag-active text-ag-active' : 'border-orange-600 text-orange-400 hover:bg-orange-900/20'}`}
                    disabled={reportStatus === 'generating'}
                    onClick={async () => {
                      setReportStatus('generating');
                      try { await fetch('/api/claw/health', { headers: apiHeaders() }); } catch {}
                      setReportStatus('done');
                      setTimeout(() => setReportStatus('idle'), 3000);
                    }}
                  >
                    {reportStatus === 'generating' ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                    ) : reportStatus === 'done' ? (
                      <><CheckCircle className="w-4 h-4 mr-2" />Report Ready</>
                    ) : (
                      <><FileText className="w-4 h-4 mr-2" />Generate Report</>
                    )}
                  </Button>

                  <Button 
                    variant="outline" 
                    className="w-full border-red-600 text-red-400 hover:bg-red-900/20"
                    onClick={() => handleDismissThreat(selectedThreat.id)}
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    Dismiss Threat
                  </Button>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardContent className="p-8 text-center">
                <Search className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
                <h4 className="text-white font-medium mb-2">Select a threat</h4>
                <p className="text-[#A8A29E] text-sm">
                  Click on a threat from the list to view detailed analysis and enforcement options
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}